import { 
  Target,
  Lock,
  Rocket,
  Shield,
  Eye,
  Activity,
  Database,
  Network,
  CheckCircle
} from 'lucide-react';
import type { FeatureCard } from './types';

/**
 * Enterprise Security & Performance Template
 * Based on Digital Newsroom Platform configuration
 * Used to ensure consistency across all 364+ products
 * 
 * This template provides 9 standardized security cards that meet
 * enterprise requirements including SOC 2, ISO 27001, GDPR, and
 * industry-specific compliance standards.
 */
export const enterpriseSecurityTemplate: FeatureCard[] = [
  {
    icon: Target,
    title: 'SOC 2 & ISO 27001 Compliance',
    description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and industry-specific regulatory requirements',
    metrics: 'Complete regulatory compliance',
    color: 'from-indigo-500 to-purple-600'
  },
  {
    icon: Lock,
    title: 'AES-256 Encryption & Data Protection',
    description: 'End-to-end encryption for all data at rest and in transit with customer-managed encryption keys and secure key rotation',
    metrics: 'Military-grade security',
    color: 'from-green-500 to-emerald-600'
  },
  {
    icon: Rocket,
    title: 'Scalable Cloud Infrastructure',
    description: 'Enterprise-grade cloud platform with auto-scaling, global CDN, and 99.99% uptime SLA for mission-critical operations',
    metrics: '99.99% uptime guaranteed',
    color: 'from-blue-500 to-indigo-600'
  },
  {
    icon: Shield,
    title: 'DDoS Protection & Web Application Firewall',
    description: 'Advanced DDoS mitigation with AI-powered threat detection and global content delivery network for fast, secure distribution',
    metrics: '100% attack mitigation',
    color: 'from-red-500 to-pink-600'
  },
  {
    icon: Eye,
    title: 'Multi-Factor Authentication',
    description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based access controls',
    metrics: 'Zero-trust access control',
    color: 'from-purple-500 to-indigo-600'
  },
  {
    icon: Activity,
    title: 'Comprehensive Audit Trails',
    description: 'Complete logging of all user actions, data changes, and system events for compliance and security monitoring',
    metrics: 'Complete audit visibility',
    color: 'from-yellow-500 to-amber-600'
  },
  {
    icon: Database,
    title: 'Data Backup & Disaster Recovery',
    description: 'Automated hourly backups with point-in-time recovery, geo-redundant storage, and tested disaster recovery procedures',
    metrics: 'Zero data loss guarantee',
    color: 'from-emerald-500 to-green-600'
  },
  {
    icon: Network,
    title: 'Network Security Monitoring',
    description: 'Real-time network traffic analysis, intrusion detection, and automated threat response systems with 24/7 SOC coverage',
    metrics: 'Real-time threat detection',
    color: 'from-orange-500 to-red-600'
  },
  {
    icon: CheckCircle,
    title: 'Continuous Security Assessment',
    description: 'Regular penetration testing, vulnerability scanning, and security audits with automated remediation and compliance validation',
    metrics: 'Proactive security validation',
    color: 'from-cyan-500 to-blue-600'
  }
];

/**
 * Generates customized security section based on product category
 * Can be used to add industry-specific compliance mentions
 */
export const generateSecurityForCategory = (category: string): FeatureCard[] => {
  const categoryExtensions: Record<string, Partial<FeatureCard>[]> = {
    'Healthcare': [
      { title: 'HIPAA & HITRUST Compliance', description: 'Healthcare-grade compliance controls with BAA support and HITRUST certification for protected health information', metrics: 'Healthcare compliance ready' }
    ],
    'Financial & Business Intelligence': [
      { title: 'PCI-DSS Level 1 Certified', description: 'Automatic PII and payment data redaction with PCI-DSS Level 1 certification for financial data handling', metrics: 'Highest PCI compliance' }
    ],
    'BPO & Contact Centre': [
      { title: 'PCI-DSS & GDPR Ready', description: 'Call recording compliance with PCI-DSS payment card handling and GDPR data privacy controls', metrics: 'Contact centre compliance' }
    ]
  };

  const baseTemplate = [...enterpriseSecurityTemplate];
  const extensions = categoryExtensions[category];
  
  if (extensions && extensions.length > 0) {
    // Replace first card with category-specific compliance
    const firstExtension = extensions[0];
    baseTemplate[0] = {
      ...baseTemplate[0],
      ...firstExtension
    } as FeatureCard;
  }

  return baseTemplate;
};

export default enterpriseSecurityTemplate;
